/*
 * Copyright (c) 2026 Ambits Technologies LLC. All rights reserved.
 *
 * This source code is the proprietary and confidential property of
 * Ambits Technologies LLC. Unauthorized copying, modification, distribution,
 * or use of this software, via any medium, is strictly prohibited.
 *
 * MyAmbits - AI-Powered Location Safety & Social Platform
 * https://myambits.com
 */

/**
 * Pure helpers for the per-ACCOUNT MockFriend dismissal.
 *
 * Build 164 bug: dismissal was a single device-wide boolean persisted in
 * localStorage (`myambits.safeCircle`). Once any account dismissed MockFriend
 * on a device, EVERY later account on that device never saw the onboarding
 * example — a new user on a previously-used device got an empty circle. Fix:
 * scope dismissal to the user's uid, so dismissing on account A doesn't
 * suppress the example for account B on the same device.
 */

/** True when the given uid has dismissed MockFriend. Anonymous / no uid is
 *  treated as not-dismissed so a signed-out/loading state still shows the
 *  example rather than hiding it. */
export function isMockDismissedForUid(
  dismissedForUids: readonly string[] | undefined,
  uid: string | null | undefined,
): boolean {
  if (!uid) return false;
  return Array.isArray(dismissedForUids) && dismissedForUids.includes(uid);
}

/** Add uid to the dismissed set (idempotent, no duplicates). Returns the same
 *  array reference when uid is empty or already present, so callers can skip a
 *  redundant store write. */
export function addDismissedUid(
  dismissedForUids: readonly string[] | undefined,
  uid: string | null | undefined,
): string[] {
  const current = Array.isArray(dismissedForUids) ? dismissedForUids : [];
  if (!uid || current.includes(uid)) return [...current];
  return [...current, uid];
}
